import { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { keepPreviousData, useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { adminBrandsApi, adminCategoriesApi, adminProductsApi } from '../api/catalog';
import { adminBulkApi } from '../api/operations';
import type { AdminProductQueryDto, ProductStatus } from '../api/types';
import { productStatusValues } from '../api/types';
import { Column, DataTable, SearchInput, SelectFilter, Toolbar } from '../components/DataTable';
import { PageHeader } from '../components/Shell';
import { Badge, ConfirmDialog, Pagination, Spinner } from '../components/ui';
import { useToast } from '../components/Toast';
import { useAuth } from '../context/AuthContext';
import { formatDate, money, number } from '../lib/format';
import { labelOf, productStatusLabels } from '../lib/labels';
import { P } from '../lib/permissions';

export function ProductsPage() {
  const navigate = useNavigate();
  const toast = useToast();
  const queryClient = useQueryClient();
  const { can } = useAuth();

  const [query, setQuery] = useState<AdminProductQueryDto>({ page: 1, pageSize: 20 });
  const [selected, setSelected] = useState<Set<string | number>>(new Set());
  const [deleteId, setDeleteId] = useState<number | null>(null);

  const products = useQuery({
    queryKey: ['admin', 'products', query],
    queryFn: () => adminProductsApi.search(query),
    placeholderData: keepPreviousData,
  });

  const categories = useQuery({
    queryKey: ['admin', 'categories'],
    queryFn: () => adminCategoriesApi.tree(),
    staleTime: 5 * 60_000,
  });

  const brands = useQuery({
    queryKey: ['admin', 'brands', 'all'],
    queryFn: () => adminBrandsApi.search({ pageSize: 100 }),
    staleTime: 5 * 60_000,
  });

  const invalidate = () => queryClient.invalidateQueries({ queryKey: ['admin', 'products'] });

  const setStatus = useMutation({
    mutationFn: (status: ProductStatus) =>
      adminProductsApi.setStatus(Array.from(selected).map(Number), status),
    onSuccess: (message) => {
      toast.success(message ?? 'Ürün durumları güncellendi.');
      setSelected(new Set());
      void invalidate();
    },
    onError: (error) => toast.fromError(error, 'Durum güncellenemedi.'),
  });

  const remove = useMutation({
    mutationFn: (id: number) => adminProductsApi.remove(id),
    onSuccess: (message) => {
      toast.success(message ?? 'Ürün silindi.');
      setDeleteId(null);
      void invalidate();
    },
    onError: (error) => toast.fromError(error, 'Ürün silinemedi.'),
  });

  const restore = useMutation({
    mutationFn: (id: number) => adminProductsApi.restore(id),
    onSuccess: (message) => {
      toast.success(message ?? 'Ürün geri alındı.');
      void invalidate();
    },
    onError: (error) => toast.fromError(error, 'Ürün geri alınamadı.'),
  });

  const recalculate = useMutation({
    mutationFn: (id: number) => adminProductsApi.recalculate(id),
    onSuccess: (message) => {
      toast.success(message ?? 'Türetilmiş alanlar yeniden hesaplandı.');
      void invalidate();
    },
    onError: (error) => toast.fromError(error, 'Hesaplama yapılamadı.'),
  });

  const exportCsv = useMutation({
    mutationFn: () => adminBulkApi.exportProducts(query),
    onSuccess: (operation) => {
      toast.success('Dışa aktarma başlatıldı.', `İşlem kodu: ${operation.publicId}`);
      navigate('/toplu-islemler');
    },
    onError: (error) => toast.fromError(error, 'Dışa aktarma başlatılamadı.'),
  });

  const columns: Column<(typeof rows)[number]>[] = [
    {
      key: 'title',
      header: 'Ürün',
      cell: (row) => (
        <div className="flex items-center gap-3">
          <div className="h-10 w-10 shrink-0 overflow-hidden rounded bg-ink-50">
            {row.primaryImageUrl ? (
              <img src={row.primaryImageUrl} alt="" className="h-full w-full object-cover" />
            ) : null}
          </div>
          <div className="min-w-0">
            <Link
              to={`/urunler/${row.id}`}
              className="block truncate font-medium text-ink-900 hover:text-brand-700"
            >
              {row.title}
            </Link>
            <p className="truncate text-xs text-ink-400">
              {row.brandName ?? '—'}
              {row.primaryCategoryName ? ` · ${row.primaryCategoryName}` : ''}
            </p>
          </div>
        </div>
      ),
    },
    {
      key: 'status',
      header: 'Durum',
      cell: (row) => <Badge label={labelOf(productStatusLabels, row.status)} />,
    },
    {
      key: 'price',
      header: 'Fiyat',
      align: 'right',
      cell: (row) =>
        row.minPrice === row.maxPrice
          ? money(row.minPrice)
          : `${money(row.minPrice)} – ${money(row.maxPrice)}`,
    },
    {
      key: 'stock',
      header: 'Stok',
      align: 'right',
      hideOnMobile: true,
      cell: (row) => (
        <span className={row.totalStock <= 0 ? 'font-medium text-red-600' : 'text-ink-700'}>
          {number(row.totalStock)}
        </span>
      ),
    },
    {
      key: 'variants',
      header: 'Varyant',
      align: 'right',
      hideOnMobile: true,
      cell: (row) => number(row.variantCount),
    },
    {
      key: 'sold',
      header: 'Satış',
      align: 'right',
      hideOnMobile: true,
      cell: (row) => number(row.soldCount),
    },
    {
      key: 'created',
      header: 'Eklenme',
      hideOnMobile: true,
      cell: (row) => <span className="text-ink-500">{formatDate(row.createdAt)}</span>,
    },
    {
      key: 'actions',
      header: '',
      align: 'right',
      cell: (row) => (
        <div className="flex justify-end gap-1" onClick={(event) => event.stopPropagation()}>
          {can(P.products.update) ? (
            <button
              type="button"
              className="btn-ghost btn-sm"
              title="Fiyat aralığı, stok ve puanı yeniden hesapla"
              onClick={() => recalculate.mutate(row.id)}
            >
              Yenile
            </button>
          ) : null}
          {can(P.products.delete) ? (
            row.status === 'Archived' ? (
              <button
                type="button"
                className="btn-ghost btn-sm"
                onClick={() => restore.mutate(row.id)}
              >
                Geri al
              </button>
            ) : (
              <button
                type="button"
                className="btn-ghost btn-sm text-red-600"
                onClick={() => setDeleteId(row.id)}
              >
                Sil
              </button>
            )
          ) : null}
        </div>
      ),
    },
  ];

  const rows = products.data?.items ?? [];

  return (
    <div>
      <PageHeader
        title="Ürünler"
        description={
          products.data ? `${number(products.data.totalCount)} ürün` : 'Katalog yönetimi'
        }
        actions={
          <>
            {can(P.products.bulk) ? (
              <button
                type="button"
                className="btn-secondary"
                onClick={() => exportCsv.mutate()}
                disabled={exportCsv.isPending}
              >
                {exportCsv.isPending ? <Spinner className="h-4 w-4" /> : null}
                CSV dışa aktar
              </button>
            ) : null}
            {can(P.products.create) ? (
              <Link to="/urunler/yeni" className="btn-primary">
                Yeni ürün
              </Link>
            ) : null}
          </>
        }
      />

      <Toolbar>
        <SearchInput
          value={query.search ?? ''}
          onChange={(value) => setQuery((c) => ({ ...c, search: value || undefined, page: 1 }))}
          placeholder="Ürün adı, SKU veya barkod"
        />

        <SelectFilter
          label="Durum"
          value={query.status ?? ''}
          onChange={(value) =>
            setQuery((c) => ({ ...c, status: (value || undefined) as ProductStatus, page: 1 }))
          }
          options={productStatusValues.map((value) => ({
            value,
            label: productStatusLabels[value].text,
          }))}
        />

        <SelectFilter
          label="Kategori"
          value={query.categoryId ? String(query.categoryId) : ''}
          onChange={(value) =>
            setQuery((c) => ({ ...c, categoryId: value ? Number(value) : undefined, page: 1 }))
          }
          options={flattenCategories(categories.data ?? [])}
        />

        <SelectFilter
          label="Marka"
          value={query.brandId ? String(query.brandId) : ''}
          onChange={(value) =>
            setQuery((c) => ({ ...c, brandId: value ? Number(value) : undefined, page: 1 }))
          }
          options={(brands.data?.items ?? []).map((brand) => ({
            value: String(brand.id),
            label: brand.name,
          }))}
        />

        <div className="flex flex-col gap-1.5">
          <label className="flex items-center gap-2 text-sm text-ink-700">
            <input
              type="checkbox"
              className="h-4 w-4 rounded border-ink-300 text-brand-600"
              checked={query.lowStockOnly === true}
              onChange={(event) =>
                setQuery((c) => ({ ...c, lowStockOnly: event.target.checked || undefined, page: 1 }))
              }
            />
            Stoğu az
          </label>
          <label className="flex items-center gap-2 text-sm text-ink-700">
            <input
              type="checkbox"
              className="h-4 w-4 rounded border-ink-300 text-brand-600"
              checked={query.includeDeleted === true}
              onChange={(event) =>
                setQuery((c) => ({
                  ...c,
                  includeDeleted: event.target.checked || undefined,
                  page: 1,
                }))
              }
            />
            Silinenleri göster
          </label>
        </div>
      </Toolbar>

      {selected.size > 0 && can(P.products.publish) ? (
        <div className="mb-4 flex flex-wrap items-center gap-3 rounded-xl border border-brand-200 bg-brand-50 px-4 py-3">
          <span className="text-sm text-brand-900">{selected.size} ürün seçildi</span>
          <div className="ml-auto flex flex-wrap gap-2">
            {productStatusValues.map((status) => (
              <button
                key={status}
                type="button"
                className="btn-secondary btn-sm"
                onClick={() => setStatus.mutate(status)}
                disabled={setStatus.isPending}
              >
                {productStatusLabels[status].text} yap
              </button>
            ))}
            <button type="button" className="btn-ghost btn-sm" onClick={() => setSelected(new Set())}>
              Seçimi temizle
            </button>
          </div>
        </div>
      ) : null}

      <DataTable
        columns={columns}
        rows={rows}
        rowKey={(row) => row.id}
        isLoading={products.isLoading}
        error={products.error}
        onRetry={() => void products.refetch()}
        emptyMessage="Bu filtrelere uyan ürün yok."
        onRowClick={(row) => navigate(`/urunler/${row.id}`)}
        selectedKeys={selected}
        onToggleSelect={(key) =>
          setSelected((current) => {
            const next = new Set(current);
            if (next.has(key)) next.delete(key);
            else next.add(key);
            return next;
          })
        }
        onToggleSelectAll={(checked) =>
          setSelected(checked ? new Set(rows.map((row) => row.id)) : new Set())
        }
        footer={
          products.data ? (
            <Pagination
              page={products.data.page}
              totalPages={products.data.totalPages}
              totalCount={products.data.totalCount}
              onChange={(page) => setQuery((c) => ({ ...c, page }))}
            />
          ) : null
        }
      />

      <ConfirmDialog
        open={deleteId !== null}
        title="Ürünü sil"
        message="Ürün yumuşak silinir: kayıt korunur, vitrinden kalkar. Daha sonra geri alabilirsiniz."
        confirmLabel="Sil"
        danger
        busy={remove.isPending}
        onCancel={() => setDeleteId(null)}
        onConfirm={() => deleteId !== null && remove.mutate(deleteId)}
      />
    </div>
  );
}

/** Kategori agacini duz listeye cevirir; girinti seviyeyi gosterir. */
export function flattenCategories(
  categories: { id: number; name: string; children?: unknown[] }[],
  depth = 0,
): { value: string; label: string }[] {
  return categories.flatMap((category) => [
    { value: String(category.id), label: `${'— '.repeat(depth)}${category.name}` },
    ...flattenCategories(
      (category.children ?? []) as { id: number; name: string; children?: unknown[] }[],
      depth + 1,
    ),
  ]);
}
